import { useCallback, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { datadogRum } from '@datadog/browser-rum';
import { ToastVariants } from '@dotcom/crown-ui';
import {
  hideLoadingScreen,
  setIsSystemErrorOpen,
  setIsToasterOpen,
  setPersonalizationData,
  showLoadingScreen,
  useAppContext,
} from '../context/app-context';
import { useCardContext } from '../context/card-context';
import { updateProjectName, useInitializationDataContext } from '../context/data-context';
import { getPreview, savePersonalization } from '../services';
import { updateDraftName } from '../services/customization';
import { projectDetailsQueryOptions } from '../services/preview';
import { RetryableImageUploadError } from './use-s3-upload';
import { getTransformedPersonalizationData, isArrayPopulated, isDataPopulated, validateImages } from '../utils';
import { TransformedPersonalizationData } from '../utils/helper-settings';
import { useGeneratePreviews } from '../utils/previews/generate-preview-images';
import { getProjectTypeCode } from '../utils/utility/get-project-type-code';
import { useIsOneToMany } from './use-is-one-to-many';
import { useSystemErrorHandling } from './use-system-error-handling';

type SaveProjectOptions = {
  showLoadingScreen: boolean;
  shouldRestoreCanvas: boolean;
  generateFrontPreview: boolean;
  generateFEPreviews?: boolean;
  throwOnError?: boolean;
  onSaveSuccess?: (savedPayload: TransformedPersonalizationData[]) => void;
};

const projectSaveQueues = new Map<string, Promise<void>>();

const enqueueProjectSave = <Result>(projectId: string, operation: () => Promise<Result>): Promise<Result> => {
  const previousOperation = projectSaveQueues.get(projectId) ?? Promise.resolve();
  const currentOperation = previousOperation.catch(() => undefined).then(operation);
  projectSaveQueues.set(
    projectId,
    currentOperation.then(
      () => undefined,
      () => undefined,
    ),
  );
  return currentOperation;
};
/**
 * Custom hook used to save a project's draft name and personalization.
 * After the project is saved, the card canvas gets restored to its original state.
 * @returns callbacks to save the current project and update draft name
 */
export const useSaveProject = () => {
  const {
    initializedDataState: { data: initializedData },
    initializationDataDispatch,
  } = useInitializationDataContext();
  const { cardState } = useCardContext();
  const { generatePreviewImages } = useGeneratePreviews();
  const {
    appState: { productQuantity, cardFacesLoaded },
    appDispatch,
  } = useAppContext();
  const isOneToMany = useIsOneToMany();
  const { onSystemError } = useSystemErrorHandling();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { mutateAsync: savePersonalizationAsync, isPending: isSavingPersonalization } = useMutation({
    mutationFn: ({ formData, projectId }: { formData: Parameters<typeof savePersonalization>[0]; projectId: string }) =>
      savePersonalization(formData, projectId),
    retry: false,
    onSettled: (_data, _error, { projectId }) =>
      queryClient.invalidateQueries({ queryKey: projectDetailsQueryOptions(projectId).queryKey }),
  });
  const { mutateAsync: generatePreview, isPending: isGeneratingPreview } = useMutation({
    mutationFn: ({ projectId, formData }: { projectId: string; formData: Parameters<typeof getPreview>[1] }) =>
      getPreview(projectId, formData),
    retry: false,
    onSettled: (_data, _error, { projectId }) =>
      queryClient.invalidateQueries({ queryKey: projectDetailsQueryOptions(projectId).queryKey }),
  });
  const { mutateAsync: updateDraftNameAsync, isPending: isSavingDraftName } = useMutation({
    mutationFn: ({
      draftName,
      projectId,
      quantity,
      isDraft,
    }: {
      draftName: string;
      projectId: string;
      quantity: string;
      isDraft: boolean;
    }) => updateDraftName(draftName, projectId, quantity, isDraft),
    retry: false,
    onSettled: (_data, _error, { projectId }) =>
      queryClient.invalidateQueries({ queryKey: projectDetailsQueryOptions(projectId).queryKey }),
  });

  // Latest-value ref for initializedData so async work inside saveProject can detect a
  // mid-flight project change (e.g. user navigates from project A -> project B while a
  // transform is still awaiting).
  const initializedDataRef = useRef(initializedData);
  initializedDataRef.current = initializedData;

  const missingBackgroundImageError = t('editorView.missingBackgroundImageError');

  const saveProject = useCallback(
    async (
      options: SaveProjectOptions = {
        shouldRestoreCanvas: false,
        showLoadingScreen: true,
        generateFrontPreview: false,
      },
    ) => {
      // Guard against save firing before the fabric canvases finish rendering. `cardFacesLoaded`
      // is authoritative: card-editor.tsx flips it true only once every displayed face has signaled
      // render completion from CardFace.onReady. Until then the canvases may be empty or hold a
      // transient invalid-width (?w=0) background, which would serialize to print JSON that fails
      // isDataPopulated and pop a false System Error.
      //
      // Contract: this is intentionally a silent no-op and does NOT throw, even when
      // `throwOnError` is set. Throwing here re-introduced the "Error: undefined" RUM signature
      // on Safari/WebKit (POD v6.9.0 regression) for a benign timing gap, so callers that pass
      // `throwOnError` must only mount their trigger once `cardFacesLoaded === true`. That
      // invariant is enforced observably rather than silently: every skip emits the RUM action
      // below, so a future caller violating it surfaces as a "canvas not ready" skip in Datadog.
      if (!cardFacesLoaded) {
        datadogRum.addAction('save-personalization skipped: canvas not ready', {
          projectId: initializedData?.project_id,
        });
        return;
      }

      if (!initializedData) {
        const initializationError = new Error('Initialized data is not available');
        onSystemError(
          {
            status: 0,
            data: {
              errors: [
                {
                  code: '0',
                  description: 'Initialized data is not available',
                },
              ],
            },
          },
          {
            errorCallback: () => {
              throw initializationError;
            },
          },
        );
        if (options.throwOnError) {
          throw initializationError;
        }
        return;
      }

      // Capture the project id at invocation time so the eventual PATCH call cannot
      // target a different project than the one the caller intended to save. Must be
      // non-null here since we've already validated initializedData is present.
      const capturedProjectId = initializedData?.project_id;
      if (!capturedProjectId) {
        const projectIdMissingError = new Error('Project ID is required for save');
        if (options.throwOnError) {
          throw projectIdMissingError;
        }
        return;
      }

      /**
       * handleTransformedPersonalizationData
       */
      const projectTypeCode = getProjectTypeCode(initializedData);
      // Destructure cardState to avoid mutating the original object
      const cardStateCopy = { ...cardState };

      if (!isArrayPopulated(cardState.cardFacesList)) {
        const emptyCardFacesError = new Error('Card faces list is empty when saving project');
        onSystemError(
          {
            status: 0,
            data: {
              errors: [
                {
                  code: '0',
                  description: 'Card faces list is empty when saving project',
                },
              ],
            },
          },
          {
            errorCallback: () => {
              throw emptyCardFacesError;
            },
          },
        );
        if (options.throwOnError) {
          throw emptyCardFacesError;
        }
        return;
      }

      // Transform and fetch personalization data
      const personalizationJson = await getTransformedPersonalizationData(
        cardStateCopy,
        projectTypeCode,
        // TODO: Remove this logic when issue with one-to-many validation is fixed
        isOneToMany,
        initializedData?.variables?.template_data,
      ).catch((error) => {
        // Null/undefined rejection: do NOT wrap as `new Error(String(undefined))`
        // (which produced the "Error: undefined" RUM signature), do NOT call
        // onSystemError (which would pop the system-error dialog for a non-error).
        if (error == null) {
          datadogRum.addError(new Error('save-personalization null rejection'), {
            phase: 'transform',
            projectId: capturedProjectId,
          });
          return;
        }
        // Preserve the original Error (and its stack) instead of flattening via String().
        const transformedDataError = error instanceof Error ? error : new Error(error?.message ?? String(error));
        datadogRum.addError(transformedDataError, {
          phase: 'transform',
          projectId: capturedProjectId,
        });
        onSystemError(
          {
            status: 0,
            data: {
              errors: [
                {
                  code: '0',
                  description: error,
                },
              ],
            },
          },
          {
            errorCallback: () => {
              throw transformedDataError;
            },
          },
        );
        if (options.throwOnError) {
          throw transformedDataError;
        }
      });

      if (!personalizationJson) {
        // Error will be thrown in onSystemError.
        if (options.throwOnError) {
          throw new Error('Unable to build personalization data');
        }
        return;
      }

      if (options.showLoadingScreen) {
        showLoadingScreen(appDispatch, 'Saving your greeting...');
      }

      // confirm all card faces have valid background images
      const faces = initializedData?.variables?.template_data?.Faces;
      validateImages(faces, onSystemError, missingBackgroundImageError);
      if (!isDataPopulated(personalizationJson)) {
        // If we have an invalid background image SRC in save-personalization data or empty canvasJSON,
        // display system error and don't save
        options.showLoadingScreen && hideLoadingScreen(appDispatch);
        setIsSystemErrorOpen(appDispatch, true);
        if (options.throwOnError) {
          throw new Error('Invalid personalization data');
        }
        return;
      }
      if (options.generateFEPreviews) {
        try {
          await generatePreviewImages(personalizationJson);
        } catch (previewError) {
          datadogRum.addError(previewError instanceof Error ? previewError : new Error('preview generation failed'), {
            phase: 'generate-previews',
            projectId: capturedProjectId,
            ...(previewError instanceof RetryableImageUploadError && {
              uploadCode: previewError.code,
              uploadStatus: previewError.status,
              uploadAttempts: previewError.attempts,
            }),
          });
          // Reset the 'Saving your greeting...' overlay so the user is not stuck and can retry.
          options.showLoadingScreen && hideLoadingScreen(appDispatch);
          setIsToasterOpen(appDispatch, {
            variant: ToastVariants.Error,
            title: t('editorView.previewGenerationErrorTitle'),
            children: t(
              previewError instanceof RetryableImageUploadError
                ? 'editorView.imageUploadRetryError'
                : 'editorView.previewGenerationError',
            ),
          });
          // Reject so callers keep the user on the editor instead of navigating forward.
          throw previewError;
        }
      }

      // Mid-flight consistency check: if the active project changed between the
      // start of this saveProject call and now (transforms are async), abort rather
      // than PATCH the wrong project id. Silent + telemetry - not a user error.
      if (initializedDataRef.current?.project_id !== capturedProjectId) {
        datadogRum.addAction('save-personalization aborted: project changed mid-flight', {
          capturedProjectId,
          currentProjectId: initializedDataRef.current?.project_id,
        });
        options.showLoadingScreen && hideLoadingScreen(appDispatch);
        const midFlightError = new Error('Project changed mid-flight, save aborted');
        if (options.throwOnError) {
          throw midFlightError;
        }
        return;
      }

      return enqueueProjectSave(capturedProjectId, async () => {
        await savePersonalizationAsync({ formData: personalizationJson, projectId: capturedProjectId });
        setPersonalizationData(appDispatch, personalizationJson as TransformedPersonalizationData[]);
        options.onSaveSuccess?.(personalizationJson as TransformedPersonalizationData[]);
      })
        .then(() => {
          if (!options.generateFrontPreview) {
            return;
          }
          const frontFaceIndex = cardState.cardFacesList.findIndex((face) => face.type === 'front');
          const frontFace = personalizationJson[+frontFaceIndex];
          if (frontFace) {
            const { objects, version, backgroundImage } = frontFace.PrintJson;
            return generatePreview({
              projectId: capturedProjectId,
              formData: {
                save_preview_image: true,
                asset_type_code: 'P',
                personalization_data_json: {
                  objects,
                  version,
                  backgroundImage,
                },
              },
            });
          }
        })
        .catch((error) => {
          options.generateFEPreviews && options.showLoadingScreen && hideLoadingScreen(appDispatch);
          // Null/undefined rejection: log for observability, do NOT surface a system
          // error, do NOT throw. Prevents the "Error: undefined" RUM signature and
          // the spurious system-error dialog for a non-error.
          if (error == null) {
            datadogRum.addError(new Error('save-personalization null rejection'), {
              phase: 'save',
              projectId: capturedProjectId,
            });
            return;
          }
          // Preserve the original Error (or wrap non-Error rejection values in a
          // real Error without flattening via String()).
          const saveError = error instanceof Error ? error : new Error(error?.message ?? String(error));
          onSystemError(error, {
            errorCallback: () => {
              throw saveError;
            },
          });
          if (options.throwOnError) {
            throw saveError;
          }
        })
        .finally(() => {
          !options.generateFEPreviews && options.showLoadingScreen && hideLoadingScreen(appDispatch);
        });
    },
    [
      initializedData?.project_type_code,
      initializedData?.project_id,
      initializedData?.variables?.template_data?.Faces,
      cardState,
      cardFacesLoaded,
      onSystemError,
      savePersonalizationAsync,
      generatePreview,
    ],
  );

  /**
   * Update a project's name
   * @param draftName the new name for the project
   * @param isDraft
   */
  const saveDraftName = useCallback(
    (draftName: string, isDraft: boolean) => {
      const projectId = initializedData?.project_id;
      if (!projectId) {
        return;
      }

      return updateDraftNameAsync({ draftName, projectId, quantity: productQuantity, isDraft })
        .then(() => updateProjectName(initializationDataDispatch, draftName))
        .catch((error) => onSystemError(error));
    },
    [initializedData?.project_id, productQuantity, onSystemError, updateDraftNameAsync],
  );

  return {
    saveProject,
    saveDraftName,
    isSaving: isSavingPersonalization || isGeneratingPreview || isSavingDraftName,
  };
};
